import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { createSelector, Store } from '@ngrx/store';
import { filter, Observable, take, tap, timeout } from 'rxjs';

import { selectUserFlagsAreLoaded } from '@ninety/platform/state/app-entities/feature-flag/feature-flag-state.selectors';
import {
  holdsLegacyPartnerFields,
  selectCurrentPerson,
  selectCurrentUserPartner,
  selectHasPartnerHubAccess,
} from '@ninety/platform/state/app-global/current-person/current-person.selectors';

// LaunchDarkly can be blocked or slow, and Partner Hub has to open without it: past this, the store's mode answers.
export const PARTNER_HUB_FLAG_WAIT_MS = 3000;

// Undefined while the access mode decides the answer (legacy fields, no partner record) and the user's flags haven't loaded.
const selectPartnerHubAccessOnceModeKnown = createSelector(
  selectCurrentPerson,
  selectCurrentUserPartner,
  selectHasPartnerHubAccess,
  selectUserFlagsAreLoaded,
  (person, partner, hasAccess, userFlagsAreLoaded) => {
    const modeDecides = !partner && holdsLegacyPartnerFields(person);
    return modeDecides && !userFlagsAreLoaded ? undefined : hasAccess;
  }
);

@Injectable({
  providedIn: 'root',
})
export class PartnerHubAccessGuard {
  private hasPartnerHubAccess$ = this.store.select(selectHasPartnerHubAccess);
  constructor(
    private store: Store,
    private router: Router
  ) {}

  canActivate(): Observable<boolean> {
    return this.store.select(selectPartnerHubAccessOnceModeKnown).pipe(
      filter(hasAccess => hasAccess !== undefined),
      take(1),
      timeout({ first: PARTNER_HUB_FLAG_WAIT_MS, with: () => this.hasPartnerHubAccess$.pipe(take(1)) }),
      tap(hasAccess => {
        if (!hasAccess) this.router.navigateByUrl('/home');
      })
    );
  }
}
